import { ReactNode } from 'react'
import { useAuth } from '@/lib/AuthContext'

/**
 * Wraps the entire app. Controls access:
 *  - loading            → spinner
 *  - no session         → login screen (Sign in with Google)
 *  - session, pending   → "Access pending approval"
 *  - session, rejected  → "Access denied"
 *  - session, approved  → render the app (children)
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { loading, session, appUser, signIn, logOut } = useAuth()

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="text-[var(--color-text-muted)]">Loading…</div>
      </div>
    )
  }

  // Not logged in → login screen
  if (!session) {
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="w-full max-w-sm mx-4 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-2xl shadow-xl p-8 text-center">
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Trade Manager</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-2 mb-8">
            Sign in to access your portfolio.
          </p>
          <button
            onClick={signIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg border border-[var(--color-border)] bg-white text-gray-800 font-medium hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>
        </div>
      </div>
    )
  }

  // Logged in but not approved
  if (!appUser || appUser.status !== 'approved') {
    const email = session.user?.email || ''
    const isRejected = appUser?.status === 'rejected'
    return (
      <div className="h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <div className="w-full max-w-sm mx-4 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-2xl shadow-xl p-8 text-center">
          <div className="text-4xl mb-3">{isRejected ? '🚫' : '⏳'}</div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)]">
            {isRejected ? 'Access Denied' : 'Access Pending Approval'}
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-2">
            {isRejected
              ? 'Your account is not authorized to use this application.'
              : 'Your login has been recorded. An administrator must approve your account before you can access the app.'}
          </p>
          <p className="text-xs text-[var(--color-text-muted)] mt-4 font-mono">{email}</p>
          <button
            onClick={logOut}
            className="mt-6 px-4 py-2 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)] text-sm"
          >
            Sign out
          </button>
        </div>
      </div>
    )
  }

  // Approved → full app
  return <>{children}</>
}
